import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Search,
  RefreshCw,
  Lock,
  Key,
  AlertCircle,
  Info
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface WeakCipher {
  cipher: string;
  protocol: string;
  reason: string;
  severity: 'critical' | 'high' | 'medium';
}

interface TLSCipherInfo {
  domain: string;
  supportedProtocols: string[];
  supportedCiphers: string[];
  weakCiphers: WeakCipher[];
  weakProtocols: string[];
  pciCompliant: boolean;
  recommendations: string[];
  error?: string;
}

export default function WeakTLSCiphers() {
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  // Get user's scanned domains
  const { data: scans = [], isLoading: scansLoading } = useQuery({
    queryKey: ["/api/scans"],
    refetchInterval: 60000,
  }) as { data: Array<{ id: number; url: string; status: string }>, isLoading: boolean };

  // Get unique domains from scans
  const domains = Array.from(new Set(
    scans
      .filter(scan => scan.status === 'completed')
      .map(scan => scan.url)
  ));

  // Check TLS ciphers for all domains
  const { data: tlsData, isLoading: tlsLoading, refetch } = useQuery({
    queryKey: ["/api/tls-ciphers", domains.join(',')],
    queryFn: async () => {
      if (domains.length === 0) return [];
      
      const results = await apiRequest("/api/tls-ciphers", {
        method: "POST",
        body: { domains }
      });
      return results as TLSCipherInfo[];
    },
    enabled: domains.length > 0,
  });

  const isLoading = scansLoading || tlsLoading;
  const tlsResults = Array.isArray(tlsData) ? tlsData : [];

  // Filter results based on search
  const filteredResults = tlsResults.filter((result) => {
    if (!searchTerm.trim()) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      result.domain.toLowerCase().includes(searchLower) ||
      result.weakCiphers.some(c => c.cipher.toLowerCase().includes(searchLower)) ||
      result.weakProtocols.some(p => p.toLowerCase().includes(searchLower))
    );
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-400';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'high':
        return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      case 'medium':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      default:
        return <Info className="h-4 w-4 text-gray-600" />;
    }
  };

  const totalWeakCiphers = tlsResults.reduce((sum, r) => sum + r.weakCiphers.length, 0);
  const totalWeakProtocols = tlsResults.reduce((sum, r) => sum + r.weakProtocols.length, 0);
  const pciCompliantCount = tlsResults.filter(r => r.pciCompliant).length;
  const nonCompliantCount = tlsResults.length - pciCompliantCount;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Lock className="h-6 w-6 text-purple-600" />
            Weak TLS Ciphers Analysis
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            PCI DSS compliant TLS cipher analysis for your monitored domains
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh Analysis
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-white">
                  {tlsResults.length}
                </div>
                <div className="text-xs text-slate-600">Domains Analyzed</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-red-600">
                  {totalWeakCiphers}
                </div>
                <div className="text-xs text-slate-600">Weak Ciphers</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-xl font-bold text-orange-600">
                  {totalWeakProtocols}
                </div>
                <div className="text-xs text-slate-600">Weak Protocols</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${nonCompliantCount > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
                {nonCompliantCount > 0 ? (
                  <XCircle className="h-5 w-5 text-red-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <div className={`text-xl font-bold ${nonCompliantCount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {pciCompliantCount}/{tlsResults.length}
                </div>
                <div className="text-xs text-slate-600">PCI Compliant</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* PCI DSS Info Card */}
      <Card className="border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900 dark:text-blue-100">
            <Info className="h-5 w-5" />
            PCI DSS Requirements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
            <p><strong>PCI DSS 4.1 Requirements:</strong></p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>Use TLS 1.2 or higher (TLS 1.0 and 1.1 are prohibited)</li>
              <li>Disable SSL 2.0 and SSL 3.0</li>
              <li>Prohibit weak ciphers: RC4, DES, MD5, NULL, Anonymous, Export-grade</li>
              <li>Avoid deprecated ciphers: SHA-1, 3DES</li>
              <li>Use strong ciphers: AES-128 or AES-256 with SHA-256 or higher</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search domains, ciphers, or protocols..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Results */}
      {isLoading ? (
        <Card>
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-purple-600 mx-auto mb-4" />
            <p className="text-slate-600">Analyzing TLS ciphers...</p>
          </CardContent>
        </Card>
      ) : domains.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Shield className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Domains Found</h3>
            <p className="text-slate-600 dark:text-slate-400">
              Complete some scans first to analyze TLS ciphers
            </p>
          </CardContent>
        </Card>
      ) : filteredResults.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Search className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Results Found</h3>
            <p className="text-slate-600 dark:text-slate-400">
              No domains match your search criteria
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredResults.map((result) => (
            <Card key={result.domain} className={result.pciCompliant ? '' : 'border-red-200 dark:border-red-800'}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${result.pciCompliant ? 'bg-green-100' : 'bg-red-100'}`}>
                      {result.pciCompliant ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircle className="h-5 w-5 text-red-600" />
                      )}
                    </div>
                    <div>
                      <CardTitle className="text-lg">{result.domain}</CardTitle>
                      <CardDescription>
                        {result.pciCompliant ? 'PCI Compliant' : 'Not PCI Compliant'}
                      </CardDescription>
                    </div>
                  </div>
                  <Badge variant={result.pciCompliant ? 'default' : 'destructive'}>
                    {result.pciCompliant ? 'Compliant' : 'Non-Compliant'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.error ? (
                  <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                      <strong>Error:</strong> {result.error}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Supported Protocols */}
                    {result.supportedProtocols.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Key className="h-4 w-4" />
                          Supported Protocols
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {result.supportedProtocols.map((protocol) => (
                            <Badge
                              key={protocol}
                              variant={result.weakProtocols.includes(protocol) ? 'destructive' : 'default'}
                            >
                              {protocol}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Weak Protocols */}
                    {result.weakProtocols.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-red-600 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Weak Protocols (Prohibited)
                        </h4>
                        <div className="space-y-2">
                          {result.weakProtocols.map((protocol) => (
                            <div key={protocol} className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                              <Badge variant="destructive">{protocol}</Badge>
                              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                                This protocol is prohibited by PCI DSS. Use TLS 1.2 or higher.
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Weak Ciphers */}
                    {result.weakCiphers.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-red-600 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4" />
                          Weak Ciphers Detected ({result.weakCiphers.length})
                        </h4>
                        <div className="space-y-2">
                          {result.weakCiphers.map((weakCipher, index) => (
                            <div
                              key={index}
                              className={`p-3 border rounded-lg ${getSeverityColor(weakCipher.severity)}`}
                            >
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  {getSeverityIcon(weakCipher.severity)}
                                  <span className="font-mono text-sm font-medium">
                                    {weakCipher.cipher}
                                  </span>
                                </div>
                                <Badge className={getSeverityColor(weakCipher.severity)}>
                                  {weakCipher.severity.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="text-xs mt-1">
                                <p><strong>Protocol:</strong> {weakCipher.protocol}</p>
                                <p><strong>Issue:</strong> {weakCipher.reason}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Recommendations */}
                    {result.recommendations.length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Info className="h-4 w-4" />
                          Recommendations
                        </h4>
                        <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                          {result.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* PCI Compliant Message */}
                    {result.pciCompliant && result.weakCiphers.length === 0 && result.weakProtocols.length === 0 && (
                      <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                        <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                          <CheckCircle className="h-4 w-4" />
                          <p className="font-medium">TLS configuration is PCI DSS compliant</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}















